import { useState } from "react";
import { Button, Collapse, Radio, Tooltip } from "antd";
import {
  CaretUpOutlined,
  CaretDownOutlined,
  LockOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import "./style.css";

const { Panel } = Collapse;

// Rich tooltip body for pages the user's role limits.
export const RoleLockTooltip = ({ variant, title, text }) => (
  <div className={`role-lock-tooltip-body ${variant}`}>
    <span className="role-lock-tooltip-icon">
      {variant === "restricted" ? <LockOutlined /> : <EyeOutlined />}
    </span>
    <div>
      <div className="role-lock-tooltip-title">{title}</div>
      <div className="role-lock-tooltip-text">{text}</div>
    </div>
  </div>
);

const PageContentTable = ({
  pagesList,
  pageAccess,
  handleAccessChange,
  readOnly,
  disabledPages,
  viewOnlyPages,
  readOnlyTooltip,
}) => {
  return (
    <div className="page-access-table">
      <div className="page-access-table-header">
        <span>Pages Access</span>
        <span>View</span>
        <span>View & Edit</span>
        <span>Restrict</span>
      </div>

      {pagesList.map((page) => {
        // Restricted for the user's role -- can't be changed for this user.
        const roleRestricted = disabledPages.includes(page);
        // The role only grants View -- View & Edit isn't allowed.
        const roleViewOnly = viewOnlyPages.includes(page);
        // When the viewer only has view rights, explain that instead of the
        // per-page role limits (those are only shown to someone who can edit).
        const showReadOnlyTooltip = readOnly && !!readOnlyTooltip;

        return (
          <Tooltip
            key={page}
            placement="topLeft"
            color="#ffffff"
            classNames={{ root: "role-lock-tooltip" }}
            title={
              showReadOnlyTooltip ? (
                <RoleLockTooltip
                  variant="view-only"
                  title={readOnlyTooltip.title}
                  text={readOnlyTooltip.text}
                />
              ) : roleRestricted ? (
                <RoleLockTooltip
                  variant="restricted"
                  title="Restricted by role"
                  text="The user's role restricts this page, so it can't be changed for this user."
                />
              ) : null
            }
          >
            <div className="page-access-table-row">
              <span className="page-name">{page}</span>

              <Radio.Group
                value={pageAccess[page] || "view"}
                onChange={(e) => handleAccessChange(page, e.target.value)}
                disabled={readOnly || roleRestricted}
                className={`radio-group-grid ${
                  roleRestricted ? "locked-restricted" : ""
                }`}
              >
                <Radio value="view" />
                <Tooltip
                  color="#ffffff"
                  classNames={{ root: "role-lock-tooltip" }}
                  title={
                    roleViewOnly && !roleRestricted && !showReadOnlyTooltip ? (
                      <RoleLockTooltip
                        variant="view-only"
                        title="View only by role"
                        text="The user's role only allows View on this page, so View & Edit isn't available."
                      />
                    ) : null
                  }
                >
                  <Radio
                    value="view_edit"
                    disabled={readOnly || roleRestricted || roleViewOnly}
                  />
                </Tooltip>
                <Radio value="restrict" />
              </Radio.Group>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};
const GenericPageManager = ({
  pagesList,
  pageAccess,
  setPageAccess,
  childTitle,
  childSubTitle,
  childButtonContent,
  childButtonIcon,
  onChildButtonPress,
  isCollapsible,
  readOnly = false,
  disabledPages = [],
  viewOnlyPages = [],
  // Optional { title, text } shown on hover when the grid is read-only.
  readOnlyTooltip = null,
}) => {
  const [pageActiveKey, setPageActiveKey] = useState(true);

  const handleViewClick = () => {
    setPageActiveKey((prev) => !prev);
  };

  const handleAccessChange = (page, value) => {
    if (readOnly) return;
    if (disabledPages.includes(page)) return;
    if (value === "view_edit" && viewOnlyPages.includes(page)) return;
    setPageAccess({ ...pageAccess, [page]: value });
  };
  return (
    <div className="manage-pages">
      {isCollapsible ? (
        <Collapse
          bordered={false}
          activeKey={pageActiveKey ? ["0"] : []}
          expandIcon={() => null}
        >
          <Panel
            key="0"
            className="panel-style"
            header={
              <div className="collapse-header">
                <span className="collapse-title">Manage Pages</span>

                <Button
                  size="small"
                  icon={
                    pageActiveKey ? <CaretUpOutlined /> : <CaretDownOutlined />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewClick();
                  }}
                  className="view-btn"
                >
                  {pageActiveKey ? "Close" : "View"}
                </Button>
              </div>
            }
          >
            <div className="panel-content">
              <PageContentTable
                pagesList={pagesList}
                pageAccess={pageAccess}
                handleAccessChange={handleAccessChange}
                readOnly={readOnly}
                disabledPages={disabledPages}
                viewOnlyPages={viewOnlyPages}
                readOnlyTooltip={readOnlyTooltip}
              />
              {!readOnly && (
                <div className="delete-box">
                  <div>
                    <div className="delete-title">{childTitle}</div>
                    <div className="delete-sub">{childSubTitle}</div>
                  </div>

                  <Button
                    danger
                    icon={childButtonIcon}
                    onClick={onChildButtonPress}
                  >
                    {childButtonContent}
                  </Button>
                </div>
              )}
            </div>
          </Panel>
        </Collapse>
      ) : (
        <PageContentTable
          pageAccess={pageAccess}
          pagesList={pagesList}
          handleAccessChange={handleAccessChange}
          readOnly={readOnly}
          disabledPages={disabledPages}
          viewOnlyPages={viewOnlyPages}
          readOnlyTooltip={readOnlyTooltip}
        />
      )}
    </div>
  );
};

export default GenericPageManager;
